/**
 * Mention Parser Utilities
 *
 * Pure functions for serializing, deserializing, and manipulating
 * mention tokens in message text. The token format uses zero-width
 * spaces as delimiters for reliable parsing:
 *
 *   \u200B@[userId|displayName]\u200B
 *
 * These utilities are chat-context agnostic.
 */

import type { MentionData, MentionSegment, ParsedMentionText } from '@/types/mentions';

// ==================== Constants ====================

/** Zero-width space used as mention token delimiter */
const ZWS = '\u200B';

/**
 * Regex to match mention tokens in text.
 * Format: \u200B@[userId|displayName|role]\u200B  (role is optional for backward compat)
 * Uses a non-greedy match for the content between brackets.
 */
const MENTION_TOKEN_REGEX = /\u200B@\[([^|]+?)\|([^|\]]+?)(?:\|([^\]]*?))?\]\u200B/g;

// ==================== Serialization ====================

/**
 * Serialize a mention into a token string for embedding in message text.
 *
 * @param userId - The user's UID
 * @param displayName - The user's display name
 * @param role - The user's role (optional, used for notification filtering)
 * @returns The serialized mention token
 */
export function serializeMention(userId: string, displayName: string, role?: string): string {
  return `${ZWS}@[${userId}|${displayName}|${role || ''}]${ZWS}`;
}

// ==================== Deserialization ====================

/**
 * Parse message text containing mention tokens into an ordered array
 * of text and mention segments for rendering.
 *
 * @param text - Message text potentially containing mention tokens
 * @returns Parsed segments and extracted mention data
 */
export function deserializeMentions(text: string): ParsedMentionText {
  if (!text) {
    return { segments: [], mentions: [] };
  }

  const segments: MentionSegment[] = [];
  const mentions: MentionData[] = [];
  const seenUserIds = new Set<string>();

  let lastIndex = 0;

  // Reset regex state (global flag)
  MENTION_TOKEN_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = MENTION_TOKEN_REGEX.exec(text)) !== null) {
    const [fullMatch, userId, displayName, role] = match;
    const matchStart = match.index;

    // Add text segment before this mention
    if (matchStart > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, matchStart) });
    }

    // Add mention segment
    segments.push({ type: 'mention', userId, displayName });

    // Track unique mentions (include role if present)
    if (!seenUserIds.has(userId)) {
      seenUserIds.add(userId);
      mentions.push({
        userId,
        displayName,
        ...(role ? { role: role as MentionData['role'] } : {}),
      });
    }

    lastIndex = matchStart + fullMatch.length;
  }

  // Add trailing text
  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) });
  }

  // If no mentions were found, return the whole text as a single segment
  if (segments.length === 0) {
    segments.push({ type: 'text', content: text });
  }

  return { segments, mentions };
}

// ==================== Extraction ====================

/**
 * Extract just the user IDs from mention tokens in text.
 * Useful for building the plaintext `mentionedUserIds` field.
 *
 * @param text - Message text potentially containing mention tokens
 * @returns Array of unique user IDs
 */
export function extractMentionedUserIds(text: string): string[] {
  if (!text) return [];

  const userIds: string[] = [];
  const seen = new Set<string>();

  MENTION_TOKEN_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = MENTION_TOKEN_REGEX.exec(text)) !== null) {
    const userId = match[1];
    if (!seen.has(userId)) {
      seen.add(userId);
      userIds.push(userId);
    }
  }

  return userIds;
}

/**
 * Extract full mention data from mention tokens in text.
 * Useful for building the encrypted `mentions` array.
 *
 * @param text - Message text potentially containing mention tokens
 * @returns Array of unique MentionData objects
 */
export function extractMentions(text: string): MentionData[] {
  return deserializeMentions(text).mentions;
}

// ==================== Plain Text Conversion ====================

/**
 * Convert text with mention tokens to human-readable plain text.
 * Replaces tokens with "@displayName" format.
 * Useful for notification previews and copy-to-clipboard.
 *
 * @param text - Message text potentially containing mention tokens
 * @returns Plain text with @name format
 */
export function mentionToPlainText(text: string): string {
  if (!text) return '';

  MENTION_TOKEN_REGEX.lastIndex = 0;
  return text.replace(MENTION_TOKEN_REGEX, (_match, _userId: string, displayName: string) => {
    return `@${displayName}`;
  });
}

// ==================== Utility ====================

/**
 * Check if a text string contains any mention tokens.
 */
export function hasMentions(text: string): boolean {
  if (!text) return false;
  MENTION_TOKEN_REGEX.lastIndex = 0;
  return MENTION_TOKEN_REGEX.test(text);
}
